import { useState, useEffect, useMemo } from 'react';

type CountdownValues = {
  readonly years: number;
  readonly months: number;
  readonly days: number;
  readonly hours: number;
  readonly minutes: number;
};

const DEFAULT_TARGET_DATE = new Date('2028-03-15T00:00:00Z');

const computeCountdown = (target: Date): CountdownValues => {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) {
    return { years: 0, months: 0, days: 0, hours: 0, minutes: 0 };
  }
  const years = Math.floor(diff / (1000 * 60 * 60 * 24 * 365));
  const months = Math.floor((diff % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30));
  const days = Math.floor((diff % (1000 * 60 * 60 * 24 * 30)) / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return { years, months, days, hours, minutes };
};

const useCountdown = (targetDate: string | null): CountdownValues => {
  const target = useMemo(
    () => (targetDate ? new Date(targetDate) : DEFAULT_TARGET_DATE),
    [targetDate],
  );
  const [values, setValues] = useState<CountdownValues>(() => computeCountdown(target));

  useEffect(() => {
    const interval = setInterval(() => {
      setValues(computeCountdown(target));
    }, 1000);
    return (): void => { clearInterval(interval); };
  }, [target]);

  return values;
};

export { useCountdown, computeCountdown, DEFAULT_TARGET_DATE };
export type { CountdownValues };
